import { cn } from '@/lib/utils'
import { Lock, Plus, Search } from 'lucide-react'

export type StorySort = 'newest' | 'score' | 'competency'

interface StoryBankToolbarProps {
  search: string
  onSearchChange: (value: string) => void
  sort: StorySort
  onSortChange: (value: StorySort) => void
  isPro: boolean
  onAddStory: () => void
}

const SORT_OPTIONS: { key: StorySort; label: string; proOnly: boolean }[] = [
  { key: 'newest', label: 'Newest', proOnly: false },
  { key: 'score', label: 'Highest score', proOnly: true },
  { key: 'competency', label: 'Competency', proOnly: false },
]

export function StoryBankToolbar({
  search,
  onSearchChange,
  sort,
  onSortChange,
  isPro,
  onAddStory,
}: StoryBankToolbarProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search your stories…"
          aria-label="Search stories"
          className="w-full rounded-lg border border-border bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
        />
      </div>
      <div className="flex items-center gap-2">
        <div
          className="flex rounded-lg border border-border p-0.5"
          // biome-ignore lint/a11y/useSemanticElements: ARIA group on a styled div — fieldset breaks the flex pill layout
          role="group"
          aria-label="Sort stories"
        >
          {SORT_OPTIONS.map(({ key, label, proOnly }) => {
            const locked = proOnly && !isPro
            return (
              <button
                key={key}
                type="button"
                disabled={locked}
                title={locked ? 'Sorting by score is a Pro feature' : undefined}
                onClick={() => onSortChange(key)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  sort === key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                  locked && 'opacity-50 cursor-not-allowed'
                )}
              >
                {locked && <Lock className="h-2.5 w-2.5" />}
                {label}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={onAddStory}
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
        >
          <Plus className="h-3.5 w-3.5" />
          Add story
        </button>
      </div>
    </div>
  )
}
